import { Percent, Price, Token } from '@kodiak-finance/sdk-core'
import { Currency } from '@kodiak-finance/sdk-core'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import { IslandApiResponse } from 'state/data/slice'
import { Bound } from 'state/islands/mint/actions'
import { getQuoteCurrency } from 'utils/getQuoteToken'
import { getTickToPrice } from 'utils/getTickToPrice'
import { unwrappedToken } from 'utils/unwrappedToken'

export enum PoolProvider {
  KODIAK = 'kodiak',
  V2 = 'v2',
  Charm = 'charm',
}

export interface ProcessedIslandData {
  currencyQuote: Currency | null | undefined
  currencyBase: Currency | null | undefined
  priceLower: Price<Token, Token> | null | undefined
  priceUpper: Price<Token, Token> | null | undefined
  quotePrice: Price<Token, Token> | null | undefined
  positionSummaryLink: string
  currencyA?: Currency | null | undefined
  currencyB?: Currency | null | undefined
  farmAddress?: string
  isLowerPriceAtLimit: boolean | undefined
  isUpperPriceAtLimit: boolean | undefined
  feeTier?: number
}

/**
 * A hook that processes raw island data into usable price and currency information
 * @param island The island data from the API
 * @param token0 The first token in the pair
 * @param token1 The second token in the pair
 * @returns Processed island data with price calculations and currency information
 */

export function useProcessedPoolData(
  island: IslandApiResponse | undefined,
  token0: Token | null | undefined,
  token1: Token | null | undefined,
  fallbackData?: {
    lowerTick?: number
    upperTick?: number
    currentTick?: number
    feeTier?: number
    poolType?: 'Island' | 'V2' | 'V3' | 'Charm' | 'Generic'
  }
): ProcessedIslandData | null {
  const tickAtLimit = useIsTickAtLimit(
    island ? island.feeTier : fallbackData?.feeTier,
    island ? island.lowerTick : fallbackData?.lowerTick,
    island ? island.upperTick : fallbackData?.upperTick
  )

  return useMemo(() => {
    try {
      if (!token0 || !token1) {
        return null
      }

      let dataSource: 'island' | 'fallback' | null = null
      let poolProvider: 'V2' | 'V3' | 'Charm' | null = null
      let tickData: { lowerTick?: number; upperTick?: number; currentTick?: number } = {}
      let extraData: { positionSummaryLink?: string; farmAddress?: string } = {}
      if (island) {
        dataSource = 'island'
        if (island.provider === PoolProvider.V2) {
          poolProvider = 'V2'
        } else if (island.provider === PoolProvider.Charm) {
          poolProvider = 'Charm'
        } else {
          poolProvider = 'V3'
        }
        tickData = {
          lowerTick: island.lowerTick,
          upperTick: island.upperTick,
          currentTick: island.currentTick,
        }
        const baseLink = `/liquidity/pools/${island.id}`
        const farmAddress = island.farm?.id
        extraData = {
          positionSummaryLink: farmAddress ? `${baseLink}?farm=${farmAddress}` : baseLink,
          farmAddress,
        }
      } else if (fallbackData && token0 && token1) {
        dataSource = 'fallback'
        if (fallbackData.poolType === 'V2') {
          poolProvider = 'V2'
        } else if (fallbackData.poolType === 'Charm') {
          poolProvider = 'Charm'
        } else {
          poolProvider = 'V3'
        }

        tickData = {
          lowerTick: fallbackData.lowerTick,
          upperTick: fallbackData.upperTick,
          currentTick: fallbackData.currentTick,
        }
        extraData = {
          positionSummaryLink: '',
          farmAddress: undefined,
        }
      } else {
        return null
      }

      const currencyA = token0 && unwrappedToken(token0)
      const currencyB = token1 && unwrappedToken(token1)

      if (!currencyA || !currencyB) {
        return null
      }

      const currencyQuote = getQuoteCurrency(currencyA, currencyB)
      const currencyBase = currencyQuote === currencyA ? currencyB : currencyA

      let price1: Price<Token, Token> | null | undefined
      let finalMinDisplayPrice: Price<Token, Token> | null | undefined
      let isFinalMinDisplayPriceAtLimit: boolean | undefined
      let finalMaxDisplayPrice: Price<Token, Token> | null | undefined
      let isFinalMaxDisplayPriceAtLimit: boolean | undefined

      if (poolProvider === 'V2' || poolProvider === 'Charm') {
        if (dataSource === 'island') {
          // Handle V2 price calculation from island data
          const token0PriceUSD = island!.token0?.price || 0
          const token1PriceUSD = island!.token1?.price || 0
          if (token0PriceUSD > 0 && token1PriceUSD > 0) {
            const ratio = token0PriceUSD / token1PriceUSD

            const baseDecimals = token0.decimals
            const quoteDecimals = token1.decimals

            const baseAmount = JSBI.BigInt(10 ** baseDecimals)
            const quoteAmount = JSBI.BigInt(Math.round(ratio * 10 ** quoteDecimals))

            price1 = new Price(token0, token1, baseAmount, quoteAmount)
          } else {
            price1 = null
          }
        } else {
          price1 = null
        }
        finalMaxDisplayPrice = null
        finalMinDisplayPrice = null
        isFinalMinDisplayPriceAtLimit = undefined
        isFinalMaxDisplayPriceAtLimit = undefined
      } else {
        const lowerTickActual = tickData.lowerTick
        const upperTickActual = tickData.upperTick
        const currentTickActual = tickData.currentTick

        const isPoolLowerTickAtSystemLimit = tickAtLimit[Bound.LOWER]
        const isPoolUpperTickAtSystemLimit = tickAtLimit[Bound.UPPER]

        // Calculate prices in pool's native orientation
        const priceAtLowerTick_poolOriented =
          typeof lowerTickActual === 'number' ? getTickToPrice(token0, token1, lowerTickActual) : undefined
        const priceAtUpperTick_poolOriented =
          typeof upperTickActual === 'number' ? getTickToPrice(token0, token1, upperTickActual) : undefined
        const currentPrice_poolOriented =
          typeof currentTickActual === 'number' ? getTickToPrice(token0, token1, currentTickActual) : undefined

        // Convert to display orientation
        const displayOrientationRequiresInversion = currencyQuote?.wrapped.equals(token0.wrapped)

        const priceFromLowerTick_display = displayOrientationRequiresInversion
          ? priceAtLowerTick_poolOriented?.invert()
          : priceAtLowerTick_poolOriented
        const priceFromUpperTick_display = displayOrientationRequiresInversion
          ? priceAtUpperTick_poolOriented?.invert()
          : priceAtUpperTick_poolOriented

        const boundaryFromLowerTick = {
          price: priceFromLowerTick_display,
          isAtSystemLimit: isPoolLowerTickAtSystemLimit,
        }
        const boundaryFromUpperTick = {
          price: priceFromUpperTick_display,
          isAtSystemLimit: isPoolUpperTickAtSystemLimit,
        }

        let minInfo = boundaryFromLowerTick
        let maxInfo = boundaryFromUpperTick

        if (
          boundaryFromLowerTick.price &&
          boundaryFromUpperTick.price &&
          boundaryFromLowerTick.price.greaterThan(boundaryFromUpperTick.price)
        ) {
          minInfo = boundaryFromUpperTick
          maxInfo = boundaryFromLowerTick
        }

        finalMinDisplayPrice = minInfo.price
        isFinalMinDisplayPriceAtLimit = minInfo.isAtSystemLimit
        finalMaxDisplayPrice = maxInfo.price
        isFinalMaxDisplayPriceAtLimit = maxInfo.isAtSystemLimit
        price1 = getTickToPrice(token0, token1, currentTickActual)
      }

      const quotePrice =
        currencyQuote?.name === currencyA?.name || currencyQuote?.symbol === currencyA?.symbol
          ? price1?.invert()
          : price1

      return {
        currencyQuote,
        currencyBase,
        priceLower: finalMinDisplayPrice,
        priceUpper: finalMaxDisplayPrice,
        quotePrice,
        positionSummaryLink: extraData.positionSummaryLink || '',
        currencyA,
        currencyB,
        farmAddress: extraData.farmAddress,
        isLowerPriceAtLimit: isFinalMinDisplayPriceAtLimit,
        isUpperPriceAtLimit: isFinalMaxDisplayPriceAtLimit,
        feeTier: island?.feeTier ?? fallbackData?.feeTier,
      }
    } catch (e) {
      console.error('Error processing pool data:', e)
      return null
    }
  }, [token0, token1, island, fallbackData, tickAtLimit])
}

/**
 * A utility hook that returns the fee tier as a percentage
 * @param feeTier The fee tier value (e.g. 3000 for 0.3%)
 * @returns The fee tier as a percentage string
 */
export function useFeeTierPercent(feeTier: number | undefined): string {
  return useMemo(() => {
    return new Percent(feeTier || 3000, 1_000_000).toSignificant()
  }, [feeTier])
}

export function useProcessedPoolDataWithFallback(
  island: IslandApiResponse | undefined,
  token0: Token | null | undefined,
  token1: Token | null | undefined,
  poolType: string
) {
  const fallbackData = useMemo(() => {
    if (!island || poolType !== 'Island') return undefined

    return {
      lowerTick: island?.lowerTick ? parseInt(island.lowerTick.toString()) : undefined,
      upperTick: island?.upperTick ? parseInt(island?.upperTick?.toString()) : undefined,
      currentTick: island?.currentTick ? parseInt(island?.currentTick?.toString()) : undefined,
      feeTier: island?.feeTier,
      poolType: 'Island' as const,
    }
  }, [island, poolType])

  return useProcessedPoolData(island, token0, token1, fallbackData)
}
